import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { X } from 'react-bootstrap-icons';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import { ABORT_ERROR, makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import FilePopup from '../common/FilePopup';
import ContractedUnitOverview from '../inventory/ContractedUnitOverview';
import EditInventory from '../inventory/EditInventory';
import InventoryPicker from '../inventory/InventoryPicker';
import PotentialUnitsOverview from '../inventory/PotentialUnitsOverview';
import {
  getValueAtPath,
  initDocumentStateFromParams,
  initInventoryMetaFromParams,
  parseTokenPath,
  setValueAtPath
} from './documentStateHelpers';
import {
  addExpandableTableRow,
  documentLayoutHasCommissionTables,
  INVENTORY_COMMISSION_TABLE_FIELDS,
  pickCommissionTablesFromInventory,
  removeExpandableTableRow,
  snapshotCommissionTablesForInventory,
  snapshotCustomTablesForDocument,
  updateCustomTableEntry
} from './customSheetFields';
import { normalizeRefId, resolveVariableFieldBinding } from './resolveVariableField';
import { validateDocumentMilestones } from './milestoneDateHelpers';
import {
  SHEET_CHECKBOXES_FIELD,
  initSheetCheckboxes,
  mergeSheetCheckboxesForSave,
  setSheetCheckboxValue,
  snapshotSheetCheckboxes
} from './sheetCheckboxHelpers';
import SheetRenderer from './SheetRenderer';
import { getVariableSegmentsFromMeta } from './variableTokenHelpers';
import '../doc-hub/PrintGroupSelectionBox.scss';

const InventoryEditSideBar = ({ editingInventory, appChoices, onHide, onInventoryDelete, onInventoryEdit }) => {
  return (
    <SlidingSideBar fullScreen visible={Boolean(editingInventory)} onClose={onHide} title={'Inventory Details'} zIndex={10001}>
      {editingInventory && (
        <EditInventory
          id={editingInventory['_id']}
          inventoryFromSearch={editingInventory}
          appChoices={appChoices}
          fromSearch
          onInventoryDelete={onInventoryDelete}
          onInventoryEdit={onInventoryEdit}
        />
      )}
    </SlidingSideBar>
  );
};

const buildDocumentPayload = (
  documentState,
  documentFields,
  documentLayout,
  appChoices,
  existingSheetCheckboxes = null
) => {
  const payload = { ...documentState };
  delete payload.custom;

  const existingAdditionalFields = documentFields?.withData?.find(f => f.name === 'additionalFields')?.value || {};

  if (payload.additionalFields) {
    payload.additionalFields = {
      ...(typeof existingAdditionalFields === 'object' && existingAdditionalFields !== null
        ? existingAdditionalFields
        : {}),
      ...payload.additionalFields
    };
  }

  const existingCustomEntities = documentFields?.withData?.find(f => f.name === 'customEntities')?.value || {};
  if (payload.customEntities) {
    payload.customEntities = {
      ...(typeof existingCustomEntities === 'object' && existingCustomEntities !== null && !Array.isArray(existingCustomEntities)
        ? existingCustomEntities
        : {}),
      ...payload.customEntities
    };
  }

  // Always replace sheet-mapped table fields from what is on screen (including []).
  Object.assign(payload, snapshotCustomTablesForDocument(documentState, documentLayout, appChoices));

  // Merge per-sheet so printing/saving doc A then doc B keeps both sheets' checkboxes.
  const checkboxSnapshot = snapshotSheetCheckboxes(documentState, documentLayout);
  if (Object.keys(checkboxSnapshot).length) {
    payload[SHEET_CHECKBOXES_FIELD] = mergeSheetCheckboxesForSave(
      checkboxSnapshot,
      existingSheetCheckboxes
    );
  } else if (
    existingSheetCheckboxes &&
    typeof existingSheetCheckboxes === 'object' &&
    !Array.isArray(existingSheetCheckboxes)
  ) {
    // Current doc has no checkboxes — still preserve previously saved sheets.
    payload[SHEET_CHECKBOXES_FIELD] = existingSheetCheckboxes;
  }

  return payload;
};

const collectVariableTokens = documentLayout => {
  const tokens = new Set();

  (documentLayout?.sheets || []).forEach(sheet => {
    (sheet.variableCells || []).forEach(cell => {
      const segments = getVariableSegmentsFromMeta(cell, cell.rawValue);
      segments.forEach(segment => {
        if (segment.type === 'variable' && segment.fullToken) {
          tokens.add(segment.fullToken);
        }
      });
    });
  });

  return Array.from(tokens);
};

const buildInventoryPayload = ({
  documentLayout,
  appChoices,
  docSource,
  pickedInventory,
  documentState
}) => {
  if (!pickedInventory?._id) return null;

  const inventoryTokens = collectVariableTokens(documentLayout)
    .map(parseTokenPath)
    .filter(path => path?.[0] === 'inventoryUnit');

  const payload = {};

  inventoryTokens.forEach(path => {
    const binding = resolveVariableFieldBinding({
      path,
      appChoices,
      docSource,
      pickedInventory
    });
    const value = getValueAtPath(pickedInventory, binding.statePath);

    if (value === undefined) return;

    const normalizedValue = binding.type === 'ref' ? normalizeRefId(value) : value;

    if (binding.statePath.length === 1) {
      payload[binding.statePath[0]] = normalizedValue;
      return;
    }

    const rootKey = binding.statePath[0];
    const existingRoot =
      payload[rootKey] ||
      (pickedInventory[rootKey] &&
      typeof pickedInventory[rootKey] === 'object' &&
      !Array.isArray(pickedInventory[rootKey])
        ? { ...pickedInventory[rootKey] }
        : {});

    payload[rootKey] = setValueAtPath(existingRoot, binding.statePath.slice(1), normalizedValue);
  });

  if (documentLayoutHasCommissionTables(documentLayout)) {
    Object.assign(payload, snapshotCommissionTablesForInventory(documentState));
  }

  const checkboxSnapshot = snapshotSheetCheckboxes(documentState, documentLayout);
  if (Object.keys(checkboxSnapshot).length && docSource === 'Inventory') {
    payload[SHEET_CHECKBOXES_FIELD] = mergeSheetCheckboxesForSave(
      checkboxSnapshot,
      pickedInventory?.[SHEET_CHECKBOXES_FIELD]
    );
  } else if (
    docSource === 'Inventory' &&
    pickedInventory?.[SHEET_CHECKBOXES_FIELD] &&
    typeof pickedInventory[SHEET_CHECKBOXES_FIELD] === 'object'
  ) {
    payload[SHEET_CHECKBOXES_FIELD] = pickedInventory[SHEET_CHECKBOXES_FIELD];
  }

  return Object.keys(payload).length ? payload : null;
};

const DocumentPrinterV2 = ({
  appChoices,
  docSource,
  documentId,
  documentLayout,
  documentUrl,
  documentName,
  documentFields,
  selectedGroup,
  isServiceRequest,
  onUpdateDocHub
}) => {
  const [documentState, setDocumentState] = useState(() => initDocumentStateFromParams(documentFields));
  const [inventoryUnitMeta, setInventoryUnitMeta] = useState(() =>
    initInventoryMetaFromParams(documentFields, docSource)
  );
  const [savingData, setSavingData] = useState(false);
  const [printingDocument, setPrintingDocument] = useState(false);
  const [createdDocument, setCreatedDocument] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const abortControllerRef = useRef(null);
  const createdDocumentAlertRef = useRef(null);
  // Survives switching documents in-session so sheet maps from doc A + doc B merge.
  const persistedSheetCheckboxesRef = useRef(null);
  const persistedCheckboxesOwnerIdRef = useRef(null);

  const getExistingSheetCheckboxes = useCallback(() => {
    return (
      persistedSheetCheckboxesRef.current ||
      (docSource === 'Inventory'
        ? inventoryUnitMeta?.pickedInventory?.[SHEET_CHECKBOXES_FIELD]
        : selectedGroup?.[SHEET_CHECKBOXES_FIELD]) ||
      null
    );
  }, [docSource, inventoryUnitMeta?.pickedInventory, selectedGroup]);

  const rememberPersistedCheckboxes = useCallback(merged => {
    if (merged && typeof merged === 'object' && !Array.isArray(merged)) {
      persistedSheetCheckboxesRef.current = merged;
    }
  }, []);

  useEffect(() => {
    if (!createdDocument || !createdDocumentAlertRef.current) return;
    createdDocumentAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [createdDocument]);

  // Reset merge base when switching contact/contract/inventory record.
  useEffect(() => {
    const ownerId =
      docSource === 'Inventory'
        ? inventoryUnitMeta?.pickedInventory?._id
        : selectedGroup?._id;
    if (!ownerId) return;
    if (persistedCheckboxesOwnerIdRef.current === ownerId) return;
    persistedCheckboxesOwnerIdRef.current = ownerId;
    persistedSheetCheckboxesRef.current =
      (docSource === 'Inventory'
        ? inventoryUnitMeta?.pickedInventory?.[SHEET_CHECKBOXES_FIELD]
        : selectedGroup?.[SHEET_CHECKBOXES_FIELD]) || null;
  }, [
    docSource,
    inventoryUnitMeta?.pickedInventory?._id,
    inventoryUnitMeta?.pickedInventory?.[SHEET_CHECKBOXES_FIELD],
    selectedGroup?._id,
    selectedGroup?.[SHEET_CHECKBOXES_FIELD]
  ]);

  useEffect(() => {
    const meta = initInventoryMetaFromParams(documentFields, docSource);
    setInventoryUnitMeta(meta);

    const baseState = {
      ...initDocumentStateFromParams(documentFields),
      ...pickCommissionTablesFromInventory(meta?.pickedInventory)
    };

    const savedCheckboxes =
      (docSource === 'Inventory'
        ? meta?.pickedInventory?.[SHEET_CHECKBOXES_FIELD]
        : selectedGroup?.[SHEET_CHECKBOXES_FIELD]) ||
      persistedSheetCheckboxesRef.current ||
      documentFields?.withData?.find(f => f.name === SHEET_CHECKBOXES_FIELD)?.value ||
      null;

    if (savedCheckboxes) {
      persistedSheetCheckboxesRef.current = savedCheckboxes;
    }

    setDocumentState({
      ...baseState,
      [SHEET_CHECKBOXES_FIELD]: initSheetCheckboxes(documentLayout, savedCheckboxes, baseState)
    });
  }, [documentFields, docSource, documentLayout, selectedGroup?.[SHEET_CHECKBOXES_FIELD], selectedGroup?._id]);

  const pickedInventory = inventoryUnitMeta?.pickedInventory;

  // Re-seed when the linked unit changes (pick / replace).
  useEffect(() => {
    setDocumentState(prev => {
      const next = {
        ...prev,
        ...pickCommissionTablesFromInventory(pickedInventory)
      };
      if (docSource === 'Inventory' && pickedInventory) {
        const saved = pickedInventory[SHEET_CHECKBOXES_FIELD];
        if (saved) persistedSheetCheckboxesRef.current = saved;
        next[SHEET_CHECKBOXES_FIELD] = initSheetCheckboxes(documentLayout, saved, next);
      }
      return next;
    });
  }, [pickedInventory?._id, docSource, documentLayout]);

  const onDocumentPathChange = useCallback((path, value) => {
    setDocumentState(prev => setValueAtPath(prev, path, value));
  }, []);

  const onSheetCheckboxChange = useCallback((sheetName, index, checked, defaults) => {
    setDocumentState(prev => setSheetCheckboxValue(prev, sheetName, index, checked, defaults));
    if (docSource === 'Inventory') {
      setInventoryUnitMeta(prev => (prev ? { ...prev, inventoryDirty: true } : prev));
    }
  }, [docSource]);

  const markCommissionInventoryDirty = useCallback(field => {
    if (!INVENTORY_COMMISSION_TABLE_FIELDS.has(field)) return;
    setInventoryUnitMeta(prev => (prev ? { ...prev, inventoryDirty: true } : prev));
  }, []);

  const onCustomTableCellChange = useCallback(
    (field, index, prop, value) => {
      setDocumentState(prev => updateCustomTableEntry(prev, field, index, prop, value));
      markCommissionInventoryDirty(field);
    },
    [markCommissionInventoryDirty]
  );

  const onExpandableTableAddRow = useCallback(
    (field, layout) => {
      setDocumentState(prev => addExpandableTableRow(prev, field, layout));
      markCommissionInventoryDirty(field);
    },
    [markCommissionInventoryDirty]
  );

  const onExpandableTableRemoveRow = useCallback(
    (field, index) => {
      setDocumentState(prev => removeExpandableTableRow(prev, field, index));
      markCommissionInventoryDirty(field);
    },
    [markCommissionInventoryDirty]
  );

  const onInventoryPathChange = useCallback((pathFromUnit, value) => {
    setInventoryUnitMeta(prev => {
      if (!prev?.pickedInventory) return prev;
      let nextPicked = setValueAtPath(prev.pickedInventory, pathFromUnit, value);

      if (pathFromUnit[0] === 'manufacturer' && nextPicked.model) {
        nextPicked = { ...nextPicked, model: '' };
      }

      const nextUnits = Array.isArray(prev.inventoryUnits)
        ? prev.inventoryUnits.map(unit => (unit?._id === nextPicked._id ? nextPicked : unit))
        : prev.inventoryUnits;

      return {
        ...prev,
        pickedInventory: nextPicked,
        inventoryUnits: nextUnits,
        inventoryDirty: true
      };
    });
  }, []);

  const updateContactOrContractInventory = async inventoryUnit => {
    if (!selectedGroup?._id) return false;

    setInventoryUnitMeta(prev => ({ ...prev, assigningInventory: true }));

    const { error } = await makeApiRequests({
      endpoint:
        docSource === 'Contact'
          ? ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
          : ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id),
      requestBody: { inventoryUnit },
      method: 'PUT'
    });

    setInventoryUnitMeta(prev => ({ ...prev, assigningInventory: false }));

    if (error) {
      toast.error(error);
      return false;
    }

    toast.success('Inventory unit updated successfully!');
    return true;
  };

  const onLinkUnitClick = replacingInventoryUnit => {
    setInventoryUnitMeta(prev => ({
      ...prev,
      showInventoryPicker: true,
      replacingInventoryUnit
    }));
  };

  const onInventoryPickerClose = () => {
    setInventoryUnitMeta(prev => ({ ...prev, showInventoryPicker: false }));
  };

  const onEditUnitClick = unit => {
    setInventoryUnitMeta(prev => ({ ...prev, editingInventory: unit }));
  };

  const onEditUnitSideBarHide = () => {
    setInventoryUnitMeta(prev => ({ ...prev, editingInventory: null }));
  };

  const onInventoryEdit = updatedInventory => {
    setInventoryUnitMeta(prev => {
      if (!prev) return prev;
      const nextUnits = Array.isArray(prev.inventoryUnits)
        ? prev.inventoryUnits.map(unit => (unit?._id === updatedInventory._id ? updatedInventory : unit))
        : prev.inventoryUnits;

      return {
        ...prev,
        pickedInventory:
          prev.pickedInventory?._id === updatedInventory._id ? updatedInventory : prev.pickedInventory,
        inventoryUnits: nextUnits,
        editingInventory: null
      };
    });
  };

  const onInventoryDelete = deletedInventoryId => {
    setInventoryUnitMeta(prev => {
      if (!prev) return prev;
      const nextUnits = Array.isArray(prev.inventoryUnits)
        ? prev.inventoryUnits.filter(unit => unit?._id !== deletedInventoryId)
        : prev.inventoryUnits;

      return {
        ...prev,
        pickedInventory: prev.pickedInventory?._id === deletedInventoryId ? undefined : prev.pickedInventory,
        inventoryUnits: nextUnits,
        editingInventory: null
      };
    });
  };

  const removeInventoryUnitFromSelections = removingInventoryUnit => {
    setInventoryUnitMeta(prev => {
      const previousUnits = prev.inventoryUnits || [];
      const newInventoryUnits =
        docSource === 'Contact'
          ? previousUnits.filter(u => u._id !== removingInventoryUnit._id)
          : prev.inventoryUnits;
      const picked = prev.pickedInventory;
      const clearPicked =
        docSource === 'Contract' ||
        (docSource === 'Contact' && picked && picked._id === removingInventoryUnit._id);

      return {
        ...prev,
        inventoryUnits: newInventoryUnits,
        pickedInventory: clearPicked ? undefined : picked,
        deletingUnit: false,
        toBeDeletedUnit: undefined
      };
    });
  };

  const onRemoveUnitClick = removingInventoryUnit => {
    if (!window.confirm('Remove this inventory unit from the record?')) return;

    (async () => {
      setInventoryUnitMeta(prev => ({ ...prev, deletingUnit: true }));

      const inventoryUnitPayload =
        docSource === 'Contact'
          ? (inventoryUnitMeta?.inventoryUnits || [])
              .map(u => u._id)
              .filter(id => id !== removingInventoryUnit._id)
          : null;

      const { response, error } = await makeApiRequests({
        endpoint:
          docSource === 'Contact'
            ? ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
            : ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id),
        requestBody: { inventoryUnit: inventoryUnitPayload },
        method: 'PUT'
      });

      setInventoryUnitMeta(prev => ({ ...prev, deletingUnit: false }));

      if (error) {
        toast.error(error);
        return;
      }

      toast.success('Inventory unit removed successfully!');

      if (docSource === 'Contact') {
        const units = response?.inventoryUnit || [];
        setInventoryUnitMeta(prev => ({
          ...prev,
          inventoryUnits: units,
          pickedInventory:
            prev.pickedInventory && units.some(u => u._id === prev.pickedInventory._id)
              ? units.find(u => u._id === prev.pickedInventory._id)
              : units[0],
          deletingUnit: false,
          toBeDeletedUnit: undefined
        }));
      } else {
        removeInventoryUnitFromSelections(removingInventoryUnit);
      }
    })();
  };

  const onInventoryPicked = async inventoryUnits => {
    const inventoryUnit = inventoryUnits[0];
    if (!inventoryUnit) return;

    const previousUnits = inventoryUnitMeta?.inventoryUnits || [];
    const replacingInventoryUnit = inventoryUnitMeta?.replacingInventoryUnit;
    const currentPickedInventory = inventoryUnitMeta?.pickedInventory;

    let newInventoryUnits;
    if (docSource === 'Contact') {
      newInventoryUnits = [...previousUnits];
      if (replacingInventoryUnit) {
        const replacingIndex = newInventoryUnits.findIndex(u => u._id === replacingInventoryUnit._id);
        if (replacingIndex !== -1) newInventoryUnits[replacingIndex] = inventoryUnit;
        else newInventoryUnits.push(inventoryUnit);
      } else {
        newInventoryUnits.push(inventoryUnit);
      }
    }

    if (selectedGroup?._id) {
      const success = await updateContactOrContractInventory(
        docSource === 'Contact' ? newInventoryUnits.map(u => u._id) : inventoryUnit._id
      );
      if (!success) return;
    }

    setInventoryUnitMeta(prev => ({
      ...prev,
      inventoryUnits: newInventoryUnits,
      pickedInventory:
        docSource === 'Contract'
          ? inventoryUnit
          : (() => {
              const selectedId =
                replacingInventoryUnit && currentPickedInventory?._id === replacingInventoryUnit._id
                  ? inventoryUnit._id
                  : currentPickedInventory && newInventoryUnits.some(unit => unit._id === currentPickedInventory._id)
                    ? currentPickedInventory._id
                    : newInventoryUnits[0]?._id;
              return newInventoryUnits.find(unit => unit._id === selectedId);
            })(),
      showInventoryPicker: false,
      replacingInventoryUnit: undefined,
      inventoryDirty: false
    }));
  };

  const onRefreshUnitClick = async () => {
    if (!selectedGroup?._id) return;

    setInventoryUnitMeta(prev => ({ ...prev, refreshingInventory: true }));

    const { response, error } = await makeApiRequests({
      endpoint: docSource === 'Contract' ? ENDPOINTS.CONTRACTS_SEARCH : ENDPOINTS.CONTACTS_SEARCH,
      requestBody: { filter: { _id: selectedGroup._id } }
    });

    setInventoryUnitMeta(prev => ({ ...prev, refreshingInventory: false }));

    if (error) {
      toast.error(error);
      return;
    }

    const details = response?.[0];
    if (!details) return;

    const refreshedInventoryUnits = docSource === 'Contact' ? details.inventoryUnit : undefined;
    const selectedInventoryId = inventoryUnitMeta?.pickedInventory?._id;

    setInventoryUnitMeta(prev => ({
      ...prev,
      inventoryUnits: refreshedInventoryUnits,
      pickedInventory:
        docSource === 'Contract'
          ? details.inventoryUnit
          : selectedInventoryId && refreshedInventoryUnits?.some(unit => unit._id === selectedInventoryId)
            ? refreshedInventoryUnits.find(unit => unit._id === selectedInventoryId)
            : refreshedInventoryUnits?.[0],
      inventoryDirty: false
    }));
  };

  const saveInventoryEdits = useCallback(
    async ({ signal, silent = false } = {}) => {
      if (!inventoryUnitMeta?.inventoryDirty || !pickedInventory?._id) return true;

      const payload = buildInventoryPayload({
        documentLayout,
        appChoices,
        docSource,
        pickedInventory,
        documentState
      });

      if (!payload) return true;

      if (!silent) {
        toast.info('Saving linked inventory...');
      }

      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.INVENTORY_WITH_ID(pickedInventory._id),
        method: 'PUT',
        requestBody: payload,
        signal
      });

      if (error) {
        if (error !== ABORT_ERROR) {
          toast.error(error);
        }
        return false;
      }

      setInventoryUnitMeta(prev => {
        if (!prev) return prev;
        const nextUnits = Array.isArray(prev.inventoryUnits)
          ? prev.inventoryUnits.map(unit => (unit?._id === response._id ? response : unit))
          : prev.inventoryUnits;

        return {
          ...prev,
          pickedInventory: response,
          inventoryUnits: nextUnits,
          inventoryDirty: false
        };
      });

      return true;
    },
    [
      appChoices,
      docSource,
      documentLayout,
      documentState,
      inventoryUnitMeta?.inventoryDirty,
      pickedInventory
    ]
  );

  const buildPrintDocumentParams = useCallback(() => {
    const payload = buildDocumentPayload(
      documentState,
      documentFields,
      documentLayout,
      appChoices,
      getExistingSheetCheckboxes()
    );

    if (payload[SHEET_CHECKBOXES_FIELD]) {
      rememberPersistedCheckboxes(payload[SHEET_CHECKBOXES_FIELD]);
    }

    if (inventoryUnitMeta?.pickedInventory?._id) {
      payload.inventoryUnit = inventoryUnitMeta.pickedInventory._id;
    }

    if (isServiceRequest) {
      payload.serviceOrder = selectedGroup?.serviceOrderReport || [];
    }

    return payload;
  }, [
    appChoices,
    documentFields,
    documentLayout,
    documentState,
    getExistingSheetCheckboxes,
    inventoryUnitMeta,
    isServiceRequest,
    rememberPersistedCheckboxes,
    selectedGroup
  ]);

  const validateBeforePrint = useCallback(() => {
    const milestoneCheck = validateDocumentMilestones(documentState);
    if (!milestoneCheck.ok) {
      toast.error(milestoneCheck.message);
      return false;
    }

    if (!inventoryUnitMeta) return true;

    if (docSource === 'Contract' && !inventoryUnitMeta.pickedInventory) {
      toast.error('Please link an inventory unit before printing.');
      return false;
    }

    if (docSource === 'Contact') {
      if ((inventoryUnitMeta.inventoryUnits || []).length === 0) {
        toast.error('Please link potential units and pick one to print for this document.');
        return false;
      }

      if (!inventoryUnitMeta.pickedInventory) {
        toast.error('Please pick one of the potential units to be printed for this document.');
        return false;
      }
    }

    return true;
  }, [docSource, documentState, inventoryUnitMeta]);

  const onSaveClick = useCallback(async () => {
    if (!selectedGroup?._id) return;

    const milestoneCheck = validateDocumentMilestones(documentState);
    if (!milestoneCheck.ok) {
      toast.error(milestoneCheck.message);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (docSource === 'Inventory') {
      setInventoryUnitMeta(prev => (prev ? { ...prev, inventoryDirty: true } : prev));
      setSavingData(true);
      toast.info('Saving document data...');
      // inventoryDirty set above is async — save with explicit payload instead
      const payload = buildInventoryPayload({
        documentLayout,
        appChoices,
        docSource,
        pickedInventory,
        documentState
      });
      const checkboxSnapshot = snapshotSheetCheckboxes(documentState, documentLayout);
      const mergedCheckboxes = Object.keys(checkboxSnapshot).length
        ? mergeSheetCheckboxesForSave(checkboxSnapshot, getExistingSheetCheckboxes())
        : getExistingSheetCheckboxes();
      const requestBody = {
        ...(payload || {}),
        ...(mergedCheckboxes
          ? { [SHEET_CHECKBOXES_FIELD]: mergedCheckboxes }
          : {})
      };

      if (!pickedInventory?._id || !Object.keys(requestBody).length) {
        setSavingData(false);
        return;
      }

      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.INVENTORY_WITH_ID(pickedInventory._id),
        method: 'PUT',
        requestBody,
        signal: controller.signal
      });

      setSavingData(false);
      if (error === ABORT_ERROR) return;
      if (error) {
        toast.error(error);
        return;
      }

      if (mergedCheckboxes) rememberPersistedCheckboxes(mergedCheckboxes);

      setInventoryUnitMeta(prev => {
        if (!prev) return prev;
        const nextUnits = Array.isArray(prev.inventoryUnits)
          ? prev.inventoryUnits.map(unit => (unit?._id === response._id ? response : unit))
          : prev.inventoryUnits;
        return {
          ...prev,
          pickedInventory: response,
          inventoryUnits: nextUnits,
          inventoryDirty: false
        };
      });
      toast.success('Inventory saved successfully');
      if (onUpdateDocHub) onUpdateDocHub(response);
      return;
    }

    const inventorySaved = await saveInventoryEdits({ signal: controller.signal, silent: true });
    if (!inventorySaved) return;

    setSavingData(true);
    toast.info('Saving document data...');

    const requestBody = buildDocumentPayload(
      documentState,
      documentFields,
      documentLayout,
      appChoices,
      getExistingSheetCheckboxes()
    );

    const { response, error } = await makeApiRequests({
      endpoint:
        docSource === 'Contact'
          ? ENDPOINTS.CONTACTS_WITH_ID(selectedGroup._id)
          : ENDPOINTS.CONTRACTS_WITH_ID(selectedGroup._id),
      method: 'PUT',
      requestBody,
      signal: controller.signal
    });

    setSavingData(false);

    if (error === ABORT_ERROR) return;
    if (error) {
      toast.error(error);
      return;
    }

    if (requestBody[SHEET_CHECKBOXES_FIELD]) {
      rememberPersistedCheckboxes(requestBody[SHEET_CHECKBOXES_FIELD]);
    }

    toast.success(`${docSource} saved successfully`);
    if (onUpdateDocHub) {
      onUpdateDocHub(response);
    }
  }, [
    appChoices,
    docSource,
    documentFields,
    documentLayout,
    documentState,
    getExistingSheetCheckboxes,
    onUpdateDocHub,
    pickedInventory,
    rememberPersistedCheckboxes,
    saveInventoryEdits,
    selectedGroup
  ]);

  const cancelPrinting = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (printingDocument?.printSessionId) {
      makeApiRequests({
        endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT_CANCEL(printingDocument.printSessionId)
      });
    }

    setPrintingDocument(false);
    setSavingData(false);
  }, [printingDocument]);

  const onPrintClick = useCallback(async () => {
    if (printingDocument) {
      cancelPrinting();
      return;
    }

    if (!selectedGroup?._id || !validateBeforePrint()) return;

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const inventorySaved = await saveInventoryEdits({ signal: controller.signal, silent: true });
    if (!inventorySaved) return;

    const printSessionId = uuid();
    setPrintingDocument({ printSessionId });
    toast.info('Please wait, creating document...');

    const { response, error } = await makeApiRequests({
      endpoint: ENDPOINTS.DOCHUB_PRINT_DOCUMENT_WITH_ID(selectedGroup._id),
      method: 'POST',
      requestBody: {
        documentParams: buildPrintDocumentParams(),
        docId: documentId,
        documentName,
        dataSource: docSource,
        printSessionId
      },
      signal: controller.signal
    });

    setPrintingDocument(false);

    if (error === ABORT_ERROR) return;
    if (error) {
      toast.error(error);
      return;
    }

    setCreatedDocument({ originalName: response?.metadata?.originalName, ...response });
    if (onUpdateDocHub) {
      onUpdateDocHub(response?.updatedObject, true);
    }
  }, [
    buildPrintDocumentParams,
    cancelPrinting,
    docSource,
    documentId,
    documentName,
    onUpdateDocHub,
    printingDocument,
    saveInventoryEdits,
    selectedGroup,
    validateBeforePrint
  ]);

  const inventorySection = useMemo(() => {
    if (inventoryUnitMeta === null) return null;

    if (['Contract', 'Inventory'].includes(docSource)) {
      return (
        <ContractedUnitOverview
          onRefreshUnitClick={docSource === 'Contract' ? onRefreshUnitClick : undefined}
          onLinkUnitClick={docSource === 'Contract' ? onLinkUnitClick : undefined}
          onEditUnitClick={
            docSource === 'Contract' && inventoryUnitMeta.pickedInventory
              ? () => onEditUnitClick(inventoryUnitMeta.pickedInventory)
              : undefined
          }
          onRemoveUnitClick={docSource === 'Contract' ? onRemoveUnitClick : undefined}
          onChangeUnitClick={docSource === 'Contract' ? onLinkUnitClick : undefined}
          inventoryUnit={inventoryUnitMeta.pickedInventory}
          refreshing={inventoryUnitMeta.refreshingInventory}
        />
      );
    }

    return (
      <PotentialUnitsOverview
        onRefreshUnitClick={onRefreshUnitClick}
        onLinkNewUnitClick={onLinkUnitClick}
        onRemoveUnitClick={onRemoveUnitClick}
        onChangeUnitClick={onLinkUnitClick}
        onEditUnitClick={onEditUnitClick}
        inventoryUnits={inventoryUnitMeta.inventoryUnits}
        refreshing={inventoryUnitMeta.refreshingInventory}
        activeUnitId={inventoryUnitMeta.pickedInventory?._id || ''}
        onUnitSelect={unit =>
          setInventoryUnitMeta(prev => ({
            ...prev,
            pickedInventory: prev.inventoryUnits?.find(u => u._id === unit._id) || unit
          }))
        }
        appChoices={appChoices}
      />
    );
  }, [appChoices, docSource, inventoryUnitMeta]);

  if (!documentLayout?.sheets?.length) {
    return <div className="p-3 text-muted">No sheet layout returned for this document.</div>;
  }

  return (
    <div>
      {inventorySection && <div className="mb-3">{inventorySection}</div>}

      {inventoryUnitMeta !== null && (
        <InventoryPicker
          excludedUnitIds={
            docSource === 'Contract'
              ? inventoryUnitMeta.pickedInventory
                ? [inventoryUnitMeta.pickedInventory._id]
                : []
              : (inventoryUnitMeta.inventoryUnits || []).map(u => u._id)
          }
          show={Boolean(inventoryUnitMeta.showInventoryPicker)}
          onSubmit={onInventoryPicked}
          submitting={Boolean(inventoryUnitMeta.assigningInventory)}
          onInventoryPickerClose={onInventoryPickerClose}
          selectedUnits={[]}
          onUnitSelect={() => {}}
        />
      )}

      {inventoryUnitMeta !== null && (
        <InventoryEditSideBar
          appChoices={appChoices}
          editingInventory={inventoryUnitMeta?.editingInventory}
          onHide={onEditUnitSideBarHide}
          onInventoryEdit={onInventoryEdit}
          onInventoryDelete={onInventoryDelete}
        />
      )}

      <div className="dochub-document-scroll-pad">
        <SheetRenderer
          documentLayout={documentLayout}
          documentName={documentName}
          documentUrl={documentUrl}
          appChoices={appChoices}
          docSource={docSource}
          documentState={documentState}
          pickedInventory={pickedInventory}
          onDocumentPathChange={onDocumentPathChange}
          onInventoryPathChange={onInventoryPathChange}
          onCustomTableCellChange={onCustomTableCellChange}
          onExpandableTableAddRow={onExpandableTableAddRow}
          onExpandableTableRemoveRow={onExpandableTableRemoveRow}
          onSheetCheckboxChange={onSheetCheckboxChange}
        />
      </div>

      <div className="text-right dochub-sticky-actions">
        <Button
          disabled={savingData || printingDocument}
          size="sm"
          variant="success"
          className="mr-2"
          onClick={onSaveClick}
        >
          Save
        </Button>
        <Button
          disabled={savingData}
          size="sm"
          variant={printingDocument ? 'danger' : 'primary'}
          onClick={onPrintClick}
        >
          {printingDocument ? 'Printing document... Tap again to cancel' : 'Print'}
        </Button>
      </div>

      {createdDocument && (
        <>
          <div ref={createdDocumentAlertRef}>
            <Alert variant="success" className="my-3 d-flex align-items-center">
              <p className="mb-0 flex-grow-1">
                The document "{createdDocument.originalName}" has been created successfully. You may quickly access the file
                here.
              </p>

              <Button className="mx-2" variant="success" onClick={() => setViewingFile(createdDocument)}>
                Open File
              </Button>
              <X size={25} className="ml-2 hover" onClick={() => setCreatedDocument(null)} />
            </Alert>
          </div>
          {viewingFile && <FilePopup file={viewingFile} onHide={() => setViewingFile(null)} />}
        </>
      )}
    </div>
  );
};

export default DocumentPrinterV2;
